<template>
  <div
    class="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
    role="dialog"
    aria-modal="true"
    aria-labelledby="send-text-title"
  >
    <!-- Ink backdrop -->
    <div
      class="absolute inset-0 bg-[#0E1A24]/70 backdrop-blur-sm transition-opacity"
      @click="close"
    />

    <!-- Card -->
    <div
      class="v-card relative z-10 w-full max-w-lg p-7 sm:p-9"
      @keydown.esc.stop.prevent="close"
    >
      <button
        type="button"
        class="absolute right-5 top-5 text-gray-400 transition hover:text-brand-500"
        aria-label="Close"
        @click="close"
      >
        <svg
          class="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        >
          <line
            x1="6"
            y1="6"
            x2="18"
            y2="18"
          />
          <line
            x1="18"
            y1="6"
            x2="6"
            y2="18"
          />
        </svg>
      </button>

      <!-- Compose -->
      <form
        v-if="!sent"
        @submit.prevent="send"
      >
        <div class="v-eyebrow">
          <span class="v-rule-short mr-3" />The Concierge Desk
        </div>
        <h3
          id="send-text-title"
          class="v-display mt-4 text-4xl text-brand-500"
        >
          Send us a text.
        </h3>
        <p class="mt-4 font-serif text-sm leading-relaxed text-gray-700">
          Skip the search tools — tell the concierge what you're after and we'll text you back with a
          handful of ranked tee times. Club, date, players, anything goes.
        </p>

        <label class="mt-7 block">
          <span class="v-eyebrow text-gray-500">Your message</span>
          <textarea
            ref="input"
            v-model="message"
            rows="4"
            maxlength="500"
            placeholder="e.g. Looking for a 4-ball at Pebble Shore this Saturday morning…"
            class="border-secondary-500/40 mt-2 w-full resize-none border-0 border-b bg-transparent py-2 font-serif text-base text-brand-500 placeholder-gray-400 focus:border-secondary-500 focus:outline-none focus:ring-0"
          />
        </label>

        <div class="mt-2 flex items-center justify-between">
          <span class="text-[10px] uppercase tracking-[0.25em] text-gray-400">
            We'll reply to your registered mobile
          </span>
          <span class="text-[10px] tracking-[0.2em] text-gray-400">
            {{ message.length }}/500
          </span>
        </div>

        <div class="mt-8 flex items-center justify-end gap-3">
          <button
            type="button"
            class="px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-gray-500 transition hover:text-brand-500"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="submit"
            :disabled="!canSend"
            class="inline-flex items-center gap-3 bg-brand-500 px-7 py-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#F5F1E8] transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send
            <span class="h-px w-5 bg-secondary-500" />
            →
          </button>
        </div>
      </form>

      <!-- Sent confirmation -->
      <div
        v-else
        class="py-4 text-center"
      >
        <div class="border-secondary-500/50 mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full border">
          <svg
            class="h-7 w-7 text-secondary-500"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M20 6 9 17l-5-5" />
          </svg>
        </div>
        <div class="v-eyebrow text-gray-500">
          Message sent
        </div>
        <h3 class="v-display mt-3 text-3xl text-brand-500">
          The desk has it.
        </h3>
        <p class="mx-auto mt-4 max-w-sm font-serif text-base text-gray-600">
          A concierge will text you back shortly with ranked options. No need to wait by the phone —
          you'll get a notification the moment they reply.
        </p>
        <blockquote class="border-secondary-500/40 mx-auto mt-6 max-w-sm border-l-2 pl-4 text-left font-serif text-sm italic text-gray-600">
          "{{ message }}"
        </blockquote>
        <button
          type="button"
          class="mt-8 inline-flex items-center gap-3 bg-brand-500 px-8 py-3 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#F5F1E8] transition hover:bg-brand-700"
          @click="close"
        >
          Done
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SendTextModal',

  data() {
    return {
      message: '',
      sent: false,
    };
  },

  computed: {
    canSend() {
      return this.message.trim().length > 0;
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onKeydown);
    this.$nextTick(() => {
      if (this.$refs.input) this.$refs.input.focus();
    });
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
    },

    send() {
      if (!this.canSend) return;
      // Demo only — no backend. Stores the text in the local virtusDemo thread so it
      // shows up under My Requests → Messages (and triggers a simulated reply).
      this.$store.dispatch('virtusDemo/sendMessage', this.message.trim());
      this.sent = true;
      this.$emit('sent', this.message.trim());
    },

    close() {
      this.$emit('close');
    },
  },
};
</script>
